import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Trash2, List, ArrowUp, ArrowDown } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Funcionalidad {
  id: string;
  nombre: string;
  descripcion: string | null;
  ruta: string;
  icono: string | null;
  orden: number;
  created_at: string;
}

export default function Funcionalidades() {
  const [funcionalidades, setFuncionalidades] = useState<Funcionalidad[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingFuncionalidad, setEditingFuncionalidad] = useState<Funcionalidad | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newFuncionalidad, setNewFuncionalidad] = useState({
    nombre: '',
    descripcion: '',
    ruta: '',
    icono: '',
    orden: 0
  });

  useEffect(() => {
    fetchFuncionalidades();
  }, []);

  const fetchFuncionalidades = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('funcionalidades')
        .select('*')
        .order('orden');

      if (error) throw error;
      setFuncionalidades(data || []);
    } catch (error: any) {
      console.error('Error fetching funcionalidades:', error);
      setError('Error al cargar las funcionalidades');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingFuncionalidad) {
        const { error } = await supabase
          .from('funcionalidades')
          .update({
            nombre: newFuncionalidad.nombre,
            descripcion: newFuncionalidad.descripcion || null,
            ruta: newFuncionalidad.ruta,
            icono: newFuncionalidad.icono || null,
            orden: newFuncionalidad.orden
          })
          .eq('id', editingFuncionalidad.id);

        if (error) throw error;
        setSuccess('Funcionalidad actualizada correctamente');
      } else {
        const { error } = await supabase
          .from('funcionalidades')
          .insert([{
            nombre: newFuncionalidad.nombre,
            descripcion: newFuncionalidad.descripcion || null,
            ruta: newFuncionalidad.ruta,
            icono: newFuncionalidad.icono || null,
            orden: newFuncionalidad.orden
          }]);

        if (error) throw error;
        setSuccess('Funcionalidad creada correctamente');
      }

      setShowModal(false);
      setEditingFuncionalidad(null);
      setNewFuncionalidad({
        nombre: '',
        descripcion: '',
        ruta: '',
        icono: '',
        orden: 0
      });
      fetchFuncionalidades();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleEdit = (funcionalidad: Funcionalidad) => {
    setEditingFuncionalidad(funcionalidad);
    setNewFuncionalidad({
      nombre: funcionalidad.nombre,
      descripcion: funcionalidad.descripcion || '',
      ruta: funcionalidad.ruta,
      icono: funcionalidad.icono || '',
      orden: funcionalidad.orden
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar esta funcionalidad? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('funcionalidades')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSuccess('Funcionalidad eliminada correctamente');
      fetchFuncionalidades();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    
    try {
      const currentItem = funcionalidades[index];
      const prevItem = funcionalidades[index - 1];
      
      const updates = [
        { id: currentItem.id, orden: prevItem.orden },
        { id: prevItem.id, orden: currentItem.orden }
      ];
      
      for (const update of updates) {
        const { error } = await supabase
          .from('funcionalidades')
          .update({ orden: update.orden })
          .eq('id', update.id);
          
        if (error) throw error;
      }
      
      fetchFuncionalidades();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === funcionalidades.length - 1) return;
    
    try {
      const currentItem = funcionalidades[index];
      const nextItem = funcionalidades[index + 1];
      
      const updates = [
        { id: currentItem.id, orden: nextItem.orden },
        { id: nextItem.id, orden: currentItem.orden }
      ];
      
      for (const update of updates) {
        const { error } = await supabase
          .from('funcionalidades')
          .update({ orden: update.orden })
          .eq('id', update.id);
          
        if (error) throw error;
      }
      
      fetchFuncionalidades();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const filteredFuncionalidades = funcionalidades.filter(func =>
    func.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    func.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    func.ruta.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Gestión de Funcionalidades</h1>
        <button 
          onClick={() => {
            setEditingFuncionalidad(null);
            setNewFuncionalidad({
              nombre: '',
              descripcion: '',
              ruta: '',
              icono: '',
              orden: funcionalidades.length + 1
            });
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Añadir Funcionalidad
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Buscar funcionalidades..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b">
                  <th className="pb-3 font-semibold text-gray-600">Orden</th>
                  <th className="pb-3 font-semibold text-gray-600">Nombre</th>
                  <th className="pb-3 font-semibold text-gray-600">Descripción</th>
                  <th className="pb-3 font-semibold text-gray-600">Ruta</th>
                  <th className="pb-3 font-semibold text-gray-600">Icono</th>
                  <th className="pb-3 font-semibold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredFuncionalidades.map((funcionalidad, index) => (
                  <tr key={funcionalidad.id} className="border-b">
                    <td className="py-3">
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                          className="p-1 text-gray-500 hover:bg-gray-100 rounded-full disabled:opacity-30"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleMoveDown(index)}
                          disabled={index === funcionalidades.length - 1}
                          className="p-1 text-gray-500 hover:bg-gray-100 rounded-full disabled:opacity-30"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </button>
                        <span className="ml-2">{funcionalidad.orden}</span>
                      </div>
                    </td>
                    <td className="py-3">
                      <div className="flex items-center">
                        <List className="w-5 h-5 text-blue-500 mr-2" />
                        {funcionalidad.nombre}
                      </div>
                    </td>
                    <td className="py-3">{funcionalidad.descripcion || '-'}</td>
                    <td className="py-3">
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                        {funcionalidad.ruta}
                      </code>
                    </td>
                    <td className="py-3">
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                        {funcionalidad.icono || '-'}
                      </code>
                    </td>
                    <td className="py-3">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(funcionalidad)}
                          className="p-1 text-blue-500 hover:bg-blue-100 rounded-full"
                          title="Editar funcionalidad"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(funcionalidad.id)}
                          className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                          title="Eliminar funcionalidad"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredFuncionalidades.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-gray-500">
                      <List className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No se encontraron funcionalidades</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal para añadir/editar funcionalidad */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingFuncionalidad ? 'Editar Funcionalidad' : 'Nueva Funcionalidad'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre
                </label>
                <input
                  type="text"
                  required
                  value={newFuncionalidad.nombre}
                  onChange={(e) => setNewFuncionalidad({...newFuncionalidad, nombre: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  rows={3}
                  value={newFuncionalidad.descripcion}
                  onChange={(e) => setNewFuncionalidad({...newFuncionalidad, descripcion: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ruta
                </label>
                <input
                  type="text"
                  required
                  value={newFuncionalidad.ruta}
                  onChange={(e) => setNewFuncionalidad({...newFuncionalidad, ruta: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="/admin/ruta"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Icono
                </label>
                <input
                  type="text"
                  value={newFuncionalidad.icono}
                  onChange={(e) => setNewFuncionalidad({...newFuncionalidad, icono: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Nombre del icono de Lucide"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Orden
                </label>
                <input
                  type="number"
                  required
                  value={newFuncionalidad.orden}
                  onChange={(e) => setNewFuncionalidad({...newFuncionalidad, orden: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  min="0"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingFuncionalidad(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {editingFuncionalidad ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}